import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Box,
    IconButton,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { getLongDateWithYear } from '../../utils/commonMethods';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../../hooks/useResponsive';
import { Task } from '../../interfaces/TaskOverview'
import LocalStorageUtil from '../../utils/localStorageUtil';
import { storageKeys } from '../../utils/constants';
import { AssignmentTurnedInRounded } from '@mui/icons-material';
import { useTimerContext } from '../../contexts/TimerContext';
import CardCheckList from '../../assets/cardChecklist.svg?react';

interface TaskTableProps {
    filterTaskList: Task[];
    sortTable: (id: string) => void;
    getSortIcon: () => JSX.Element;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    layerData: Record<string, any>;
}

const TaskTable: React.FC<TaskTableProps> = ({
    filterTaskList,
    sortTable,
    getSortIcon,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    layerData
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isTablet } = useResponsive();
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const {
        setSelectedProjectIdForNavigation
    } = useTimerContext();

    const tableCellStyle = {
        padding: '16px',
        borderBottom: '1px solid #E7E7E7',
        fontfamily: isTablet ? 'Nunito Sans Regular600' : 'Nunito Sans Regular400',
        fontSize: isTablet ? '10px' : '16px',
        lineHeight: '32px',
        color: '#74828F',
    };

    const iconColor = theme.palette.tableHighlight.main;

    // Helper function to get protocol name from layerData
    const getProtocolName = (layerId: number) => {
        const layer = layerData?.[layerId];
        return layer?.protocol_name || "-";
    };

    const renderTableBody = () => {
        if (filterTaskList.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={8} align="center" sx={{
                        fontFamily: "Nunito Sans Regular500",
                        fontSize: "14px",
                        color: theme.palette.blueColors.main,
                    }}>
                        {t('taskOverViewScreen.noPendingTasks')}
                    </TableCell>
                </TableRow>
            );
        }
        return (
            filterTaskList.map((task: Task, index: number) => (
                <TableRow
                    key={task.id || `${task?.id}-${task?.start_date}`}
                    sx={{
                        backgroundColor: index % 2 === 0 ? '#F1F3FA' : '#FFFFFF',
                    }}
                >
                    <TableCell sx={{ width: "30px", p: 0 }}>
                        <Box sx={{
                            alignItems: "center",
                            justifyContent: "center",
                            pl: "16px",
                            display: "flex"
                        }}>
                            {!task?.medewerker_level &&
                                <AssignmentTurnedInRounded fontSize='medium' sx={{ color: theme.palette.blueColors.main }} />
                            }
                        </Box>
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                        {task?.task_name}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                        {task?.remarks || '-'}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                        {getLongDateWithYear(task?.start_date)}
                        {task?.from_time && ` ${task.from_time.substring(0, 5)}`}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                        {getLongDateWithYear(task?.end_date)}
                        {task?.to_time && ` ${task.to_time.substring(0, 5)}`}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                        {task?.address && task.address.trim() !== '' ? task.address : ''}
                        {task?.address && task.address.trim() !== '' ? ' , ' : ''}
                        {task?.municipality}
                    </TableCell>
                    {/* <TableCell sx={tableCellStyle}>{task?.fauna_type_name}</TableCell> */}
                    <TableCell sx={tableCellStyle}>{getProtocolName(task?.layer_id)}</TableCell>
                    <TableCell sx={{
                        maxWidth: 200,
                    }}>
                        <Typography sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-all',
                            fontfamily: isTablet ? 'Nunito Sans Regular600' : 'Nunito Sans Regular400',
                            fontSize: isTablet ? '10px' : '16px',
                            lineHeight: '32px',
                            color: '#74828F',
                        }}>
                            {task?.materials}
                        </Typography>
                        {/* {task?.material_String} */}
                    </TableCell>
                    <TableCell>
                        {!task?.is_static ? (
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setSelectedProjectIdForNavigation(task?.project_id)
                                    navigate(`/taskdetail/${task?.id}`)
                                }}
                                sx={{ color: iconColor }}
                            >
                                <CardCheckList style={{ width: 16, height: 16, cursor: 'pointer' }} />
                            </IconButton>
                        ) : null}
                    </TableCell>
                </TableRow>
            ))
        )
    };

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const [target] = entries;
        if (target.isIntersecting && hasNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, isFetchingNextPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '100px',
            threshold: 0.1,
        });

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [handleObserver]);

    return (
        <TableContainer
            sx={{
                border: '1px solid #E7E7E7',
                borderRadius: 0,
                boxShadow: 'none',
                borderBottom: 'none',
                overflow: "hidden",
                // width: '100%',
                // maxWidth: '100vw'
            }}
            component={Paper}
        >
            <Table>
                <TableHead>
                    <TableRow>
                        {[
                            { id: 'avatar', label: '', sort_lable: "" },
                            { id: 'task', label: 'Taak', sort_lable: "task_name" },
                            { id: 'remarks', label: 'PM Description', sort_lable: "remarks" },
                            { id: 'start_date', label: 'Start', sort_lable: "start_date" },
                            { id: 'end_date', label: 'Eind', sort_lable: "end_date" },
                            { id: 'address', label: 'Adres', sort_lable: "address" },
                            // { id: 'fauna_type', label: 'Fauna', sort_lable: "fauna_type_name" },
                            { id: 'protocol', label: 'Protocol', sort_lable: "protocol_name" },
                            { id: 'material_String', label: 'Materiaal', sort_lable: "materials" },
                        ].map((column) => (
                            <TableCell
                                key={column.id}
                                sx={{
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: isTablet ? '14px' : '18px',
                                        fontWeight: 700,
                                        fontFamily: 'Nunito Sans Regular700',
                                        lineHeight: '32px',
                                    }}
                                >
                                    {column.label}
                                    {column.id !== "avatar" && column.id !== "protocol" && (
                                        <Box onClick={() => sortTable(column.sort_lable)} sx={{ ml: 1 }}>
                                            {getSortIcon()}
                                        </Box>
                                    )}
                                </Box>
                            </TableCell>
                        ))}
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderTableBody()}
                </TableBody>
            </Table>
            {isFetchingNextPage && (
                <Box sx={{
                    display: 'flex', justifyContent: 'center', p: 1,
                    backgroundColor: '#FFFFFF',
                    border: '0.5px solid rgba(0, 0, 0, 0.1)',
                }}>
                    <CircularProgress />
                </Box>
            )}
            <div ref={loadMoreRef} style={{ height: 1 }} />
        </TableContainer>
    );
};

export default TaskTable;